import { useState, lazy, Suspense } from "react";
import { GlobalTracking } from "@/components/GlobalTracking";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SplashScreen } from "@/components/SplashScreen";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import MainLayout from "@/components/MainLayout";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";
import Onboarding from "./pages/Onboarding";

// Lazy load all routes for better code splitting
const Profile = lazy(() => import("./pages/Profile"));
const CheckInOut = lazy(() => import("./pages/CheckInOut"));
const Rewards = lazy(() => import("./pages/Rewards"));
const Recent = lazy(() => import("./pages/Recent"));
const BorderMap = lazy(() => import("./pages/BorderMap"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const PromosLocales = lazy(() => import("./pages/PromosLocales"));
const Auth = lazy(() => import("./pages/Auth"));
const Privacy = lazy(() => import("./pages/PrivacyPolicyPage"));
const Terms = lazy(() => import("./pages/TermsPage"));
const Support = lazy(() => import("./pages/Support"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const RewardsTerms = lazy(() => import("./pages/RewardsTerms"));
const AccountDeletion = lazy(() => import("./pages/AccountDeletion"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Settings = lazy(() => import("./pages/Settings"));
import { usePlatform } from "./hooks/use-platform";
import { useAuth } from "./contexts/AuthContext";
import { useOnboarding } from "./hooks/useOnboarding";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalMadison } from "@/components/GlobalMadison";

// Lazy load heavy components and admin routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Chat = lazy(() => import("./pages/Chat"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminPromosAnalytics = lazy(() => import("./pages/AdminPromosAnalytics"));
const AdminPromos = lazy(() => import("./pages/AdminPromos"));
const AdminPromosManagement = lazy(() => import("./pages/AdminPromosManagement"));
const AdminAdvertiserProfile = lazy(() => import("./pages/AdminAdvertiserProfile"));
const AdminMonitoring = lazy(() => import("./pages/AdminMonitoring"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const queryClient = new QueryClient();

const SmartHomePage = () => {
  const { isNative, isWeb } = usePlatform();
  const { user, loading: authLoading } = useAuth();
  const { hasSeenOnboarding, loading: onboardingLoading } = useOnboarding();
  const navigate = useNavigate();
  
  // Only show splash on first app load, not on navigation
  const [showSplash, setShowSplash] = useState(() => {
    if (!isWeb) return false;
    const hasShownSplash = sessionStorage.getItem('splash_shown');
    return !hasShownSplash;
  });
  
  // Redirect to onboarding if user is logged in but hasn't seen it
  useEffect(() => {
    if (user && !onboardingLoading && hasSeenOnboarding === false) {
      navigate('/onboarding');
    }
  }, [user, hasSeenOnboarding, onboardingLoading, navigate]);
  
  // Marketing website for non-authenticated web visitors (no splash delay)
  if (!authLoading && !user && isWeb) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Marketing />
      </Suspense>
    );
  }

  // Show splash screen for web users only (native has its own)
  if (showSplash && isWeb) {
    return <SplashScreen onComplete={() => {
      sessionStorage.setItem('splash_shown', 'true');
      setShowSplash(false);
    }} minDuration={2000} />;
  }

  // Native, non-authenticated users see the in-app landing page
  if (!authLoading && !user) {
    return <LandingPage />;
  }

  // Show dashboard for authenticated users
  return <Home />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <GlobalTracking />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GlobalMadison />
        <Routes>
          <Route path="/" element={<SmartHomePage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          } />
          <Route path="/checkin" element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <MainLayout>
                  <CheckInOut />
                </MainLayout>
              </ProtectedRoute>
            </Suspense>
          } />
          <Route path="/profile" element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            </Suspense>
          } />
          <Route path="/rewards" element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <Rewards />
              </ProtectedRoute>
            </Suspense>
          } />
          <Route path="/recent" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <Recent />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/map" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <BorderMap />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/promoslocales" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <PromosLocales />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/chat" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <Chat />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/admin/login" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <AdminLogin />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/admin/promos-analytics" element={
            <Suspense fallback={<PageLoader />}>
              <AdminProtectedRoute>
                <MainLayout>
                  <AdminPromosAnalytics />
                </MainLayout>
              </AdminProtectedRoute>
            </Suspense>
          } />
          <Route path="/admin/promos" element={
            <Suspense fallback={<PageLoader />}>
              <AdminProtectedRoute>
                <MainLayout>
                  <AdminPromos />
                </MainLayout>
              </AdminProtectedRoute>
            </Suspense>
           } />
           <Route path="/admin/promos-management" element={
            <Suspense fallback={<PageLoader />}>
              <AdminProtectedRoute>
                <MainLayout>
                  <AdminPromosManagement />
                </MainLayout>
              </AdminProtectedRoute>
            </Suspense>
           } />
           <Route path="/admin/advertisers/:advertiserId" element={
             <Suspense fallback={<PageLoader />}>
               <AdminProtectedRoute>
                 <MainLayout>
                   <AdminAdvertiserProfile />
                 </MainLayout>
               </AdminProtectedRoute>
             </Suspense>
           } />
           <Route path="/admin/monitoring" element={
             <Suspense fallback={<PageLoader />}>
               <AdminProtectedRoute>
                 <MainLayout>
                   <AdminMonitoring />
                 </MainLayout>
               </AdminProtectedRoute>
             </Suspense>
           } />
           <Route path="/admin/*" element={
             <Suspense fallback={<PageLoader />}>
               <AdminProtectedRoute>
                 <MainLayout>
                   <AdminDashboard />
                 </MainLayout>
               </AdminProtectedRoute>
             </Suspense>
           } />
          <Route path="/auth" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <Auth />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/privacy" element={
            <Suspense fallback={<PageLoader />}>
              <Privacy />
            </Suspense>
          } />
          <Route path="/terms" element={
            <Suspense fallback={<PageLoader />}>
              <Terms />
            </Suspense>
          } />
          <Route path="/support" element={
            <Suspense fallback={<PageLoader />}>
              <SupportPage />
            </Suspense>
          } />
          <Route path="/help" element={
            <Suspense fallback={<PageLoader />}>
              <MainLayout>
                <Support />
              </MainLayout>
            </Suspense>
          } />
          <Route path="/community-guidelines" element={
            <Suspense fallback={<PageLoader />}>
              <CommunityGuidelines />
            </Suspense>
          } />
          <Route path="/rewards-terms" element={
            <Suspense fallback={<PageLoader />}>
              <RewardsTerms />
            </Suspense>
          } />
          <Route path="/account-deletion" element={
            <Suspense fallback={<PageLoader />}>
              <AccountDeletion />
            </Suspense>
          } />
          <Route path="/home" element={
            <Suspense fallback={<PageLoader />}>
              <Marketing />
            </Suspense>
          } />
          <Route path="/settings" element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            </Suspense>
          } />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<SmartHomePage />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
